"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { DaylightUnit } from "@/lib/daylightUnits";

type DaylightUnitContextValue = {
  selectedUnit: DaylightUnit;
  setSelectedUnit: (unit: DaylightUnit) => void;
};

const STORAGE_KEY = "cb-daylight-unit";

const DaylightUnitContext = createContext<DaylightUnitContextValue | undefined>(undefined);

export function DaylightUnitProvider({ children }: { children: React.ReactNode }) {
  const [selectedUnit, setSelectedUnitState] = useState<DaylightUnit>("footcandle");

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "footcandle" || stored === "lux") {
      setSelectedUnitState(stored);
    }
  }, []);

  const setSelectedUnit = (unit: DaylightUnit) => {
    setSelectedUnitState(unit);
    window.localStorage.setItem(STORAGE_KEY, unit);
  };

  const value = useMemo(
    () => ({
      selectedUnit,
      setSelectedUnit,
    }),
    [selectedUnit]
  );

  return <DaylightUnitContext.Provider value={value}>{children}</DaylightUnitContext.Provider>;
}

export function useDaylightUnit() {
  const context = useContext(DaylightUnitContext);
  if (!context) {
    throw new Error("useDaylightUnit must be used within DaylightUnitProvider");
  }
  return context;
}
